import { Injectable } from '@angular/core';
import { AppConfigService } from '../../services/app-config.service';
import { select, Store } from '@ngrx/store';
import { UserState } from '../../../statemanagement/user.state';
import { ConfigService } from '../../services/config/config.service';
import { MyRouter } from '../../../common/services/router.service';
import { combineLatest } from 'rxjs';
import { CorporateClientState } from '../../../statemanagement/corporate-client.state';

@Injectable({providedIn: 'root'})
export class IntercomService {
    private static url = 'https://widget.intercom.io/widget/ozi4xp8q';
    private static promise;

    constructor(private appConfigService: AppConfigService,
                private store: Store<{ user: UserState; corporateClient: CorporateClientState }>,
                private router: MyRouter) {
    }

    load() {
        if (this.appConfigService.getAppConfig().intercom.enabled) {
            if (!IntercomService.promise) {
                this.loadIntercom();
            }

            return IntercomService.promise;
        }
    }

    shutdown() {
        if (this.appConfigService.getAppConfig().intercom.enabled) {
            if (IntercomService.promise) {
                IntercomService.promise = null;
            }
            this.callIntercomMethod('shutdown');
        }
    }

    private loadIntercom() {
        IntercomService.promise = new Promise(resolve => {
            const script = document.createElement('script');
            script.src = IntercomService.url;
            script.type = 'text/javascript';
            script.async = true;
            document.getElementsByTagName('head')[0].appendChild(script);
            script.onload = () => {

                combineLatest([
                    this.store.pipe(select('user')),
                    this.store.pipe(select('corporateClient'))
                ]).subscribe({
                    next: ([user, client]: [UserState, CorporateClientState]) => {
                        if (user.loggedIn) {
                            const isProduction = ConfigService.get('isProduction');
                            const key = isProduction
                                ? this.appConfigService.getAppConfig().intercom.key
                                : this.appConfigService.getAppConfig().intercom.testKey;
                            if (key) {
                                this.callIntercomMethod('update', {
                                    app_id: key,
                                    name: user.name,
                                    user_id: user.userId,
                                    email: user.email,
                                    language_override: localStorage.getItem('language'),
                                    company: {
                                        id: client.corporateClientId,
                                        name: client.corporateClientName
                                    },
                                    'branch': user.branch,
                                    hide_default_launcher: isProduction
                                        ? !this.appConfigService.getAppConfig().intercom.chat
                                        : !this.appConfigService.getAppConfig().intercom.testChat
                                });

                                resolve(true);
                            } else {
                                this.shutdown();
                                resolve(false);
                            }
                        } else {
                            this.shutdown();
                            resolve(false);
                        }
                    }, error: () => this.shutdown()
                });
            };
        });
    }

    getWelcomeTour() {
        this.router.navigateWithCorporateClient(['/orders']);
        const NL01TourIds = {PROD: {nl: 263239, en: 263301, de: 263337, fr: 263338}, QA: {en: 261036}};
        const BE01TourIds = {PROD: {nl: 263330, en: 263324, de: 263332, fr: 263328, QA: {en: 123456}}};
        const DE01TourIds = {PROD: {en: 263334, de: 263336}, QA: {en: 123456}};
        switch (this.appConfigService.getAppConfig().opCo) {
            case 'NL01':
                return this.loadFeatureTour(NL01TourIds[ConfigService.get('isProduction') ? 'PROD' : 'QA'][localStorage.getItem('language')]
                    || NL01TourIds[ConfigService.get('isProduction') ? 'PROD' : 'QA'].en);
            case 'BE01':
                return this.loadFeatureTour(BE01TourIds[ConfigService.get('isProduction') ? 'PROD' : 'QA'][localStorage.getItem('language')]
                    || BE01TourIds[ConfigService.get('isProduction') ? 'PROD' : 'QA'].en);
            case 'DE01':
                return this.loadFeatureTour(DE01TourIds[ConfigService.get('isProduction') ? 'PROD' : 'QA'][localStorage.getItem('language')]
                    || DE01TourIds[ConfigService.get('isProduction') ? 'PROD' : 'QA'].en);

        }
    }

    loadFeatureTour(id: string) {
        this.callIntercomMethod('update');
        this.callIntercomMethod('startTour', id);
    }

    private callIntercomMethod(method: string, parameter: any = null): void {
        if ((window as any).Intercom && typeof (window as any).Intercom === 'function') {
            (window as any).Intercom(method, parameter);
        }
    }
}
